import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes } from '@angular/router';
import { map } from 'rxjs';
import { AuthService } from './core/auth.service';
import { DiscoveryAssignmentStore } from './discovery-assignment/discovery-assignment.store';
import { DiscoveryPageTools } from './discovery-assignment/discovery-page-tools.service';
import { InsightStore } from './insight/insight.store';
import { safeReturnUrl } from './core/return-url';
import { decisionDemoReturnUrls, decisionDemoRoutes } from './decision-demo/decision-demo.routes';
import { DiscoverySphereStore } from './discovery-sphere/discovery-sphere.store';

export const authenticatedGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.authState().pipe(
    map((user) =>
      user
        ? true
        : router.createUrlTree(['/login'], {
            queryParams: { returnUrl: safeReturnUrl(state.url, decisionDemoReturnUrls) },
          }),
    ),
  );
};

const authorizedGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth
    .isAuthorized()
    .pipe(map((authorized) => (authorized ? true : router.createUrlTree(['/unauthorized']))));
};

export const discoveryCreatorGuard: CanActivateFn = async () => {
  const commands = inject(DiscoveryPageTools);
  const router = inject(Router);
  try {
    const session = await commands.dispatch('get_discovery_session', {});
    return session.uid && session.authorized && session.creator
      ? true
      : router.createUrlTree(['/unauthorized']);
  } catch {
    return router.createUrlTree(['/unauthorized']);
  }
};

const loggedOutGuard: CanActivateFn = (route) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth
    .authState()
    .pipe(
      map((user) =>
        user
          ? router.parseUrl(
              safeReturnUrl(route.queryParamMap.get('returnUrl'), decisionDemoReturnUrls),
            )
          : true,
      ),
    );
};

export const routes: Routes = [
  ...decisionDemoRoutes.map((route) =>
    route.redirectTo ? route : { ...route, canActivate: [authenticatedGuard, authorizedGuard] },
  ),
  {
    path: '',
    loadComponent: () =>
      import('./console-shell/console-shell.component').then((m) => m.ConsoleShellComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'requests' },
      {
        path: 'login',
        canActivate: [loggedOutGuard],
        loadComponent: () => import('./login/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'requests/prepare',
        canActivate: [authenticatedGuard, discoveryCreatorGuard],
        loadComponent: () =>
          import('./discovery-assignment/discovery-preparation.component').then(
            (m) => m.DiscoveryPreparationComponent,
          ),
      },
      {
        path: 'requests',
        canActivate: [authenticatedGuard, authorizedGuard],
        providers: [DiscoveryAssignmentStore],
        loadComponent: () =>
          import('./discovery-assignment/discovery-assignment.component').then(
            (m) => m.DiscoveryAssignmentComponent,
          ),
      },
      {
        path: 'problems',
        pathMatch: 'full',
        canActivate: [authenticatedGuard, authorizedGuard],
        providers: [DiscoveryAssignmentStore],
        loadComponent: () => import('./problem/problem.component').then((m) => m.ProblemComponent),
      },
      {
        path: 'problems/:problemKey',
        canActivate: [authenticatedGuard, authorizedGuard],
        providers: [DiscoveryAssignmentStore],
        loadComponent: () =>
          import('./discovery-assignment/discovery-assignment.component').then(
            (m) => m.DiscoveryAssignmentComponent,
          ),
      },
      {
        path: 'insights',
        canActivate: [authenticatedGuard, authorizedGuard],
        providers: [InsightStore],
        loadComponent: () => import('./insight/insight.component').then((m) => m.InsightComponent),
      },
      {
        path: 'insights/:insightId',
        canActivate: [authenticatedGuard, authorizedGuard],
        providers: [InsightStore],
        loadComponent: () => import('./insight/insight.component').then((m) => m.InsightComponent),
      },
      {
        path: 'spheres',
        canActivate: [authenticatedGuard, authorizedGuard],
        providers: [DiscoverySphereStore],
        loadComponent: () =>
          import('./discovery-sphere/discovery-spheres.component').then(
            (m) => m.DiscoverySpheresComponent,
          ),
      },
      {
        path: 'spheres/:sphereId',
        canActivate: [authenticatedGuard, authorizedGuard],
        providers: [DiscoverySphereStore],
        loadComponent: () =>
          import('./discovery-sphere/discovery-sphere.component').then(
            (m) => m.DiscoverySphereComponent,
          ),
      },
      {
        path: 'work',
        pathMatch: 'full',
        redirectTo: 'spheres',
      },
      {
        path: 'work/:sphereId',
        redirectTo: 'spheres/:sphereId',
      },
      {
        path: 'agentic/decisions/:assignmentId',
        canActivate: [authenticatedGuard, authorizedGuard],
        loadComponent: () =>
          import('./decision-card/decision-card.component').then((m) => m.DecisionCardComponent),
      },
      {
        path: 'decisions/:assignmentId',
        redirectTo: 'agentic/decisions/:assignmentId',
      },
      {
        path: 'unauthorized',
        canActivate: [authenticatedGuard],
        loadComponent: () =>
          import('./unauthorized/unauthorized.component').then((m) => m.UnauthorizedComponent),
      },
      { path: '**', redirectTo: 'requests' },
    ],
  },
];
